import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import api from "../lib/api";
import PlaceCard from "../components/PlaceCard";
import MapView from "../components/MapView";
import CategoryIcon from "../components/CategoryIcon";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Search, Map as MapIcon, List, X } from "lucide-react";
import { CATEGORIES, getCategoryBySlug, PRIORITY_CITIES, OTHER_CITIES } from "../lib/categories";

const PRICE_OPTIONS = ["$", "$$", "$$$", "$$$$"];

export default function CategoryPage() {
  const { category: slug } = useParams();
  const entry = useMemo(() => getCategoryBySlug(slug), [slug]);
  const [params, setParams] = useSearchParams();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subtypes, setSubtypes] = useState([]);
  const [highlightedId, setHighlightedId] = useState(null);
  const [mobileView, setMobileView] = useState("list");

  const q = params.get("q") || "";
  const city = params.get("city") || "";
  const subtype = params.get("subtype") || "";
  const price = params.get("price_range") || "";

  useEffect(() => {
    if (!entry) return;
    const [key] = entry;
    api
      .get("/places/filters/options", { params: { category: key } })
      .then(({ data }) => setSubtypes(data.subtypes || []))
      .catch(() => {});
  }, [entry]);

  useEffect(() => {
    if (!entry) return;
    const [key] = entry;
    setLoading(true);
    const query = { category: key };
    if (q) query.q = q;
    if (city) query.city = city;
    if (subtype) query.subtype = subtype;
    if (price) query.price_range = price;
    api
      .get("/places", { params: query })
      .then(({ data }) => setPlaces(data))
      .catch(() => setPlaces([]))
      .finally(() => setLoading(false));
  }, [entry, q, city, subtype, price]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());

  const hasFilters = q || city || subtype || price;

  const headline = useMemo(() => {
    if (loading) return "Chargement...";
    if (places.length === 0) return "Aucun résultat";
    return `${places.length} ${places.length > 1 ? "annonces" : "annonce"}`;
  }, [places, loading]);

  if (!entry) return <Navigate to="/" replace />;
  const [catKey, cat] = entry;

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-sm"
          style={{ backgroundColor: cat.color }}
        >
          <CategoryIcon name={cat.icon} className="w-6 h-6" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-0.5" style={{ color: cat.color }}>
            Catégorie
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-ink tracking-tight">
            {cat.label} au Tchad
          </h1>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-brand-line rounded-2xl p-4 sm:p-5 mb-6 shadow-[0_8px_32px_rgba(45,42,38,0.04)]">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2 flex items-center gap-2 px-3 bg-brand-sandlight rounded-xl">
            <Search className="w-4 h-4 text-brand-inkMuted" />
            <Input
              value={q}
              onChange={(e) => updateParam("q", e.target.value)}
              placeholder={`Rechercher un ${cat.singular.toLowerCase()}...`}
              className="border-0 bg-transparent shadow-none focus-visible:ring-0 h-11"
              data-testid="filter-search-input"
            />
          </div>
          <Select value={city || "all"} onValueChange={(v) => updateParam("city", v)}>
            <SelectTrigger className="h-11 rounded-xl border-brand-line" data-testid="filter-city">
              <SelectValue placeholder="Ville" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les villes</SelectItem>
              <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-brand-terracotta font-bold">
                Prioritaires
              </div>
              {PRIORITY_CITIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
              <div className="px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-wider text-brand-inkMuted font-bold">
                Autres
              </div>
              {OTHER_CITIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={subtype || "all"} onValueChange={(v) => updateParam("subtype", v)}>
            <SelectTrigger className="h-11 rounded-xl border-brand-line" data-testid="filter-subtype">
              <SelectValue placeholder={cat.subtypeLabel} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{`Tous · ${cat.subtypeLabel}`}</SelectItem>
              {subtypes.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={price || "all"} onValueChange={(v) => updateParam("price_range", v)}>
            <SelectTrigger className="h-11 rounded-xl border-brand-line" data-testid="filter-price">
              <SelectValue placeholder="Prix" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les prix</SelectItem>
              {PRICE_OPTIONS.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
          <div className="text-sm font-medium text-brand-inkSoft" data-testid="results-count">{headline}</div>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              className="text-brand-terracotta hover:bg-brand-sandlight rounded-full"
              data-testid="clear-filters"
            >
              <X className="w-4 h-4 mr-1" /> Effacer les filtres
            </Button>
          )}
        </div>
      </div>

      {/* Mobile toggle */}
      <div className="lg:hidden flex gap-2 mb-4">
        <Button
          variant={mobileView === "list" ? "default" : "outline"}
          onClick={() => setMobileView("list")}
          className={`flex-1 rounded-full ${mobileView === "list" ? "bg-brand-terracotta hover:bg-brand-terracottaHover" : "border-brand-line"}`}
          data-testid="mobile-view-list"
        >
          <List className="w-4 h-4 mr-2" /> Liste
        </Button>
        <Button
          variant={mobileView === "map" ? "default" : "outline"}
          onClick={() => setMobileView("map")}
          className={`flex-1 rounded-full ${mobileView === "map" ? "bg-brand-terracotta hover:bg-brand-terracottaHover" : "border-brand-line"}`}
          data-testid="mobile-view-map"
        >
          <MapIcon className="w-4 h-4 mr-2" /> Carte
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-6">
        <div className={mobileView === "map" ? "hidden lg:block" : ""}>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white border border-brand-line rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-[16/10] bg-brand-sandlight" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-brand-sandlight rounded w-3/4" />
                    <div className="h-3 bg-brand-sandlight rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : places.length === 0 ? (
            <div className="bg-white border border-brand-line rounded-2xl p-10 text-center">
              <CategoryIcon name={cat.icon} className="w-12 h-12 mx-auto mb-4 text-brand-inkMuted" />
              <h3 className="font-heading text-xl font-semibold text-brand-ink mb-2">
                Aucun {cat.singular.toLowerCase()} trouvé
              </h3>
              <p className="text-brand-inkSoft">
                Essayez d'élargir vos critères ou ajoutez la première annonce.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {places.map((p, i) => (
                <div key={p.id} className="fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                  <PlaceCard
                    place={p}
                    onHover={setHighlightedId}
                    onLeave={() => setHighlightedId(null)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={`${mobileView === "list" ? "hidden lg:block" : ""} h-[70vh] lg:h-[calc(100vh-260px)] lg:sticky lg:top-24`}>
          <MapView places={places} highlightedId={highlightedId} />
        </div>
      </div>
    </div>
  );
}
